import React, { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { useRegional, formatCurrency, formatDate } from '@/lib/regional';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input, Label, Select } from '@/components/ui/input';
import { toast } from 'sonner';

export const RegionalSettings: React.FC = () => {
  const [doc, setDoc] = useState<any>(null);
  const [busy, setBusy] = useState(false);
  const { reload } = useRegional();
  useEffect(() => { api.get('/settings/regional').then(r => setDoc(r.data)); }, []);
  if (!doc) return <div>Loading…</div>;
  const save = async (): Promise<void> => {
    setBusy(true);
    try { await api.put('/settings/regional', doc); await reload(); toast.success('Regional settings saved · formatters updated'); }
    catch { toast.error('Save failed'); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader><CardTitle>Regional</CardTitle><CardDescription>Timezone, locale, currency, number &amp; date formats.</CardDescription></CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-2 gap-4">
            <div className="space-y-1.5"><Label>Timezone</Label>
              <Select value={doc.timezone} onChange={(e) => setDoc({ ...doc, timezone: e.target.value })}>
                {['Africa/Johannesburg','UTC','Europe/London','America/New_York','Asia/Dubai','Asia/Singapore'].map(t => <option key={t}>{t}</option>)}
              </Select>
            </div>
            <div className="space-y-1.5"><Label>Locale</Label>
              <Select value={doc.locale} onChange={(e) => setDoc({ ...doc, locale: e.target.value })}>
                {['en-ZA','en-GB','en-US','af-ZA','zu-ZA'].map(t => <option key={t}>{t}</option>)}
              </Select>
            </div>
            <div className="space-y-1.5"><Label>Date format</Label>
              <Select value={doc.date_format} onChange={(e) => setDoc({ ...doc, date_format: e.target.value })}>
                <option>DD/MM/YYYY</option><option>MM/DD/YYYY</option><option>YYYY-MM-DD</option><option>ISO 8601</option>
              </Select>
            </div>
            <div className="space-y-1.5"><Label>Time format</Label>
              <Select value={doc.time_format} onChange={(e) => setDoc({ ...doc, time_format: e.target.value })}><option value="24h">24h</option><option value="12h">12h</option></Select>
            </div>
            <div className="space-y-1.5"><Label>First day of week</Label>
              <Select value={doc.first_day_of_week} onChange={(e) => setDoc({ ...doc, first_day_of_week: e.target.value })}><option value="monday">Monday</option><option value="sunday">Sunday</option><option value="saturday">Saturday</option></Select>
            </div>
            <div className="space-y-1.5"><Label>Currency code</Label>
              <Select value={doc.currency_code} onChange={(e) => setDoc({ ...doc, currency_code: e.target.value })}>
                {['ZAR','USD','GBP','EUR'].map(t => <option key={t}>{t}</option>)}
              </Select>
            </div>
            <div className="space-y-1.5"><Label>Currency symbol</Label><Input value={doc.currency_symbol} onChange={(e) => setDoc({ ...doc, currency_symbol: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>Symbol position</Label>
              <Select value={doc.currency_symbol_position} onChange={(e) => setDoc({ ...doc, currency_symbol_position: e.target.value })}><option value="before">Before amount</option><option value="after">After amount</option></Select>
            </div>
            <div className="space-y-1.5"><Label>Decimal separator</Label><Input value={doc.decimal_separator} onChange={(e) => setDoc({ ...doc, decimal_separator: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>Thousands separator</Label><Input value={doc.thousands_separator} onChange={(e) => setDoc({ ...doc, thousands_separator: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>Decimal places</Label><Input type="number" value={doc.decimal_places} onChange={(e) => setDoc({ ...doc, decimal_places: parseInt(e.target.value || '0', 10) })} /></div>
            <div className="space-y-1.5"><Label>Default country code</Label><Input value={doc.default_country_code} onChange={(e) => setDoc({ ...doc, default_country_code: e.target.value })} /></div>
          </div>
          <div className="mt-4 flex justify-end gap-2"><Button variant="gradient" onClick={save} disabled={busy}>Save</Button></div>
        </CardContent>
      </Card>
      <Card>
        <CardHeader><CardTitle>Preview</CardTitle><CardDescription>Live formatter output using current settings.</CardDescription></CardHeader>
        <CardContent className="font-tabular text-raptor-navy space-y-1">
          <div>Currency: <b>{formatCurrency(1234567.89, doc)}</b></div>
          <div>Currency (small): <b>{formatCurrency(45.5, doc)}</b></div>
          <div>Date: <b>{formatDate(new Date().toISOString(), doc)}</b></div>
          <div>Number format sample: <b>1{doc.thousands_separator}234{doc.decimal_separator}56</b></div>
        </CardContent>
      </Card>
    </div>
  );
};
